/**
 * Creates a custom map legend for Leaflet maps
 * @param {Object} options - Configuration options
 * @param {string} options.title - Title of the legend
 * @param {Array} options.items - Array of legend items with either color or image properties and label
 * @param {string} options.position - Position of the legend (default: 'bottomleft')
 * @returns {L.Control} - Leaflet control object for the legend
 */
export function createCustomMapLegend(options) {
    // Default options
    const defaults = {
        title: "Map Legend",
        items: [],
        position: "bottomleft"
    };
    
    // Merge options with defaults
    const config = { ...defaults, ...options };
    
    // Create a custom legend control
    const customLegend = L.control({ position: config.position });
    
    customLegend.onAdd = function() {
        // Create the legend container
        const legendDiv = L.DomUtil.create("div", "m-t-10");
        
        // Build the legend HTML
        let legendHtml = `
            <div class="arcis-map-legend-div">
                <h4>${config.title}</h4>
                <table>
        `;
        
        // Add items to the legend
        config.items.forEach(item => {
            // Check if the item has an image or a color
            if (item.image) {
                // Image-based legend item
                legendHtml += `
                    <tr>
                        <td class="p-r-10"><img src="${item.image}" alt="${item.label}" width="${item.width || 20}" height="${item.height || 20}"></td>
                        <td>${item.label}</td>
                    </tr>
                `;
            } else if (item.color) {
                // Color-based legend item
                legendHtml += `
                    <tr>
                        <td><div style="width: 20px; height: 20px; background-color: ${item.color};"></div></td>
                        <td>${item.label}</td>
                    </tr>
                `;
            } else {
                // Fallback if neither image nor color is provided
                console.warn("Legend item missing both color and image properties:", item);
                legendHtml += `
                    <tr>
                        <td><div style="width: 20px; height: 20px; background-color: #ccc;"></div></td>
                        <td>${item.label}</td>
                    </tr>
                `;
            }
        });
        
        legendHtml += `
                </table>
            </div>
        `;
        
        legendDiv.innerHTML = legendHtml;
        
        // Initially hide the legend
        legendDiv.toggleAttribute("hidden");
        
        // Create toggle button
        const legendToggle = L.DomUtil.create("button", "btn btn-xs");
        legendToggle.innerHTML = "Legenda";
        
        // Add click event to toggle legend visibility
        L.DomEvent.addListener(legendToggle, "click", function() {
            legendDiv.toggleAttribute("hidden");
        });
        
        // Create container for button and legend
        const container = L.DomUtil.create("div");
        container.appendChild(legendToggle);
        container.appendChild(legendDiv);
        
        return container;
    };
    
    return customLegend;
}

/**
 * Fetches and creates a legend from an ArcGIS service
 * @param {Object} options - Configuration options
 * @param {string} options.url - URL of the ArcGIS service
 * @param {number} options.layerId - ID of the layer to fetch legend for (default: 1)
 * @param {string} options.position - Position of the legend (default: 'bottomleft')
 * @param {Function} options.onSuccess - Callback function when legend is successfully fetched
 * @param {Function} options.onError - Callback function when legend fetch fails
 * @returns {Promise} - Promise that resolves with the legend control
 */
export function fetchArcGISLegend(options) {
    // Default options
    const defaults = {
        url: "",
        layerId: 1,
        position: "bottomleft",
        onSuccess: null,
        onError: null
    };
    
    // Merge options with defaults
    const config = { ...defaults, ...options };
    
    // Create a promise to handle the async fetch
    return new Promise((resolve, reject) => {
        // Construct the legend URL
        const legendUrl = `${config.url}/legend?f=pjson`;
        
        // Fetch the legend data
        fetch(legendUrl)
            .then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
                return response.json();
            })
            .then(data => {
                // Find the layer with the specified ID
                const layer = data.layers.find(l => l.layerId === config.layerId);
                
                if (!layer) {
                    throw new Error(`Layer with ID ${config.layerId} not found in legend data`);
                }
                
                // Create a custom legend control
                const customLegend = L.control({ position: config.position });
                
                customLegend.onAdd = function() {
                    // Create the legend container
                    const legendDiv = L.DomUtil.create("div", "m-t-10");
                    
                    // Build the legend HTML
                    let legendHtml = `
                        <div class="arcis-map-legend-div">
                            <h4>${layer.layerName}</h4>
                    `;
                    
                    // Add legend groups
                    if (layer.legendGroups && layer.legendGroups.length > 0) {
                        layer.legendGroups.forEach(group => {
                            if (group.heading) {
                                legendHtml += `<h5>${group.heading}</h5>`;
                            }
                            
                            legendHtml += `<table>`;
                            
                            // Add legend items for this group
                            const groupItems = layer.legend.filter(item => item.groupId === group.id);
                            groupItems.forEach(item => {
                                legendHtml += `
                                    <tr>
                                        <td class="p-r-10"><img src="data:${item.contentType};base64,${item.imageData}" alt="${item.label}" width="${item.width}" height="${item.height}"></td>
                                        <td>${item.label}</td>
                                    </tr>
                                `;
                            });
                            
                            legendHtml += `</table>`;
                        });
                    } else {
                        // If no groups, just add all legend items
                        legendHtml += `<table>`;
                        layer.legend.forEach(item => {
                            legendHtml += `
                                <tr>
                                    <td class="p-r-10"><img src="data:${item.contentType};base64,${item.imageData}" alt="${item.label}" width="${item.width}" height="${item.height}"></td>
                                    <td>${item.label}</td>
                                </tr>
                            `;
                        });
                        legendHtml += `</table>`;
                    }
                    
                    legendHtml += `</div>`;
                    legendDiv.innerHTML = legendHtml;
                    
                    // Initially hide the legend
                    legendDiv.toggleAttribute("hidden");
                    
                    // Create toggle button
                    const legendToggle = L.DomUtil.create("button", "btn btn-xs");
                    legendToggle.innerHTML = "Legenda";
                    
                    // Add click event to toggle legend visibility
                    L.DomEvent.addListener(legendToggle, "click", function() {
                        legendDiv.toggleAttribute("hidden");
                    });
                    
                    // Create container for button and legend
                    const container = L.DomUtil.create("div");
                    container.appendChild(legendToggle);
                    container.appendChild(legendDiv);
                    
                    return container;
                };
                
                // Call success callback if provided
                if (config.onSuccess) {
                    config.onSuccess(customLegend);
                }
                
                // Resolve the promise with the legend control
                resolve(customLegend);
            })
            .catch(error => {
                console.error("Error fetching ArcGIS legend:", error);
                
                // Call error callback if provided
                if (config.onError) {
                    config.onError(error);
                }
                
                // Reject the promise with the error
                reject(error);
            });
    });
} 